import { ClickAwayListener, Popper } from '@mui/material'
import React, { type FC, type MutableRefObject, type ReactNode, useState } from 'react'
import classNames from 'classnames'
import { type TooltipProps } from '@mui/material/Tooltip'
import { type PopperOwnProps } from '@mui/material/Popper/BasePopper.types'
import { ZIndex } from 'shared/constants/zIndex'
import styles from './styles.module.scss'

export type ITriggerTooltipProps<T = PopperOwnProps['anchorEl']> = {
  trigger: T | undefined
  onLeaveTooltip?: () => void
  refVisibleTooltip?: MutableRefObject<boolean>
  component?: ReactNode
  color?: 'black' | 'white'
  arrow?: boolean
  parent?: HTMLDivElement | null
  open?: boolean
  placement?: TooltipProps['placement']
  onClickAway?: () => void
  marginBottom?: number | string
  overflowClassName?: string
  id?: string
}

export const TriggerTooltip: FC<ITriggerTooltipProps> = ({
  trigger,
  refVisibleTooltip,
  onLeaveTooltip,
  component,
  color = 'black',
  arrow,
  parent,
  open,
  placement = 'top',
  onClickAway,
  marginBottom,
  overflowClassName,
  id,
}) => {
  const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null)

  const openPopper = open ?? Boolean(trigger)

  const onMouseEnter = () => {
    setTimeout(() => {
      if (refVisibleTooltip) {
        refVisibleTooltip.current = true
      }
    })
  }

  if (!trigger) {
    return <></>
  }

  const content = (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onLeaveTooltip}
      className={classNames(styles.wrap, color && styles[color])}
      style={{ marginBottom }}
    >
      {arrow && <span className={styles.arrow} ref={setArrowRef} />}
      {component}
    </div>
  )

  return (
    <Popper
      open={openPopper}
      anchorEl={trigger}
      placement={placement}
      sx={{ zIndex: ZIndex.OVERLAY_MIDDLE }}
      disablePortal={false}
      id={id}
      modifiers={[
        {
          name: 'arrow',
          enabled: arrow,
          options: {
            element: arrowRef,
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: parent,
          },
        },
      ]}
    >
      <div className={overflowClassName}>
        {onClickAway ? (
          <ClickAwayListener onClickAway={onClickAway}>{content}</ClickAwayListener>
        ) : (
          content
        )}
      </div>
    </Popper>
  )
}
